import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import {
  ApiServer,
  CommonSettingsInterface,
  DefaultSelectionInterface,
  DependencyType,
  DropdownFilterConfiguration,
  DropdownSettingsInterface,
} from './dropdown/dropdown.model';
import {
  TypeFilterableObjects,
} from './filterable-objects.class';
import { FilterDateRangePickerInterface } from './filter-date-range-picker/filter-date-range-picker.model';
import { Type } from '@angular/core';
import { DropdownComponent } from './dropdown/dropdown.component';
import { FilterDateRangePickerComponent } from './filter-date-range-picker/filter-date-range-picker.component';
import { SearchBoxComponent } from './search-box/search-box.component';
import { HttpOptionInterface } from '../../../store/filter/filter.model';
import { UserGetOneCRUDDataInterface } from '../../service/user/user.model';
import { AdvancedFilterComponent } from './advanced-filter/advanced-filter.component';
import { AdvancedFilterObjects } from './advanced-filter/advanced-filter.model';
import { ButtonGroupComponent } from './button-group/button-group.component';
import { ScwMatButtonGroupButtons } from '../scw-mat-ui/scw-mat-button-group/scw-mat-button-group.model';
import { DropdownOptionInterface } from '../scw-mat-ui/scw-mat-select/scw-mat-select.model';

export type FilterDataObjectTypes =
  | KeyValueDropdownInterface
  | ObservationCRUDInterface
  | UserGetOneCRUDDataInterface
  | IUserObject
  | IEquipmentObject
  | DropdownOptionInterface
  | UsersCRUDInterface
  | DeviceTypeCRUDInterface
  | ClientCRUDInterface;

export type FilterDataServiceCRUDTypes =
  | ObservationCRUDInterface
  | DeviceTypeCRUDInterface
  | UsersCRUDInterface;

export interface DefaultSelectionValuesInterface {
  [key: string]: DefaultSelectionInterface;
}

export interface FilterClassInterface {
  objectNameProp: string;
  data: FilterDataObjectTypes[];
  dataSubject: Subject<FilterDataObjectTypes[]> | BehaviorSubject<FilterDataObjectTypes[]>;
  apiServer: ApiServer;

  setApiServer(server: ApiServer);

  getDataObservable(): Observable<FilterDataObjectTypes[]>;

  getObjectNameProp(): string;

  getInitialData(): FilterDataObjectTypes[];

  getFilterData(options: HttpOptionInterface): void;

  getDataWithInitialData(initialData: FilterDataObjectTypes[]): FilterDataObjectTypes[];
}

export interface FilterComponentInterface {
  elementID: string;
  outputOptions: OutputOptionsInterface | SimpleOutputOptionsInterface;
  filterListenerConfiguration?: DropdownFilterConfiguration[];
  disabledSubject: BehaviorSubject<boolean>;
  submit?: boolean;
  defaultSettings?: DropdownSettingsInterface | FilterDateRangePickerInterface | any;
  dropdownObjectClass?: TypeFilterableObjects;
  depends?: DependencyType;
  dropdownSettings?: DropdownSettingsInterface;
  dependedOptionListenerConfiguration?: any[];

  subscribeFilterListener?(config: DropdownFilterConfiguration): void;

  filterData?(data: any): void;

  publishSelectedItems?(): void;

  getFiltersOutputs(): any;

  subscribeDependedOptionListener?(config: any): void;
}

export interface KeyValueDropdownInterface {
  id?: string | number;
  key: string | number;
  name?: string;
}

export interface FilterCardOptionInterface {
  rows: FilterCardOptionRowInterface;
}

export interface FilterCardOptionRowInterface {
  [index: number]: RowConfigurationInterface[];
}

export interface OutputOptionsInterface {
  filterObjectId: string;
  filterObjectProp: string;
  returnFilterObjectAllProp?: boolean;
}

export interface SimpleOutputOptionsInterface {
  filterObjectId: string;
}

export interface DisableComponentInterface {
  elementId: string;
}

export interface RowConfigurationInterface {
  type: Type<
    | DropdownComponent
    | FilterDateRangePickerComponent
    | SearchBoxComponent
    | AdvancedFilterComponent
    | ButtonGroupComponent
  >;
  object?: any;
  advancedFilterObject?: Type<AdvancedFilterObjects>;
  cls?: string;
  outputOptions: OutputOptionsInterface | SimpleOutputOptionsInterface;
  options?:
    | DropdownSettingsInterface
    | SearchBoxSettingsInterface
    | FilterDateRangePickerInterface
    | ButtonGroupSettingsInterface;
  elementId?: string;
  depends?: DependencyType;
  disableComponent?: DisableComponentInterface;
  filter?: DropdownItemFilterInterface;
  dependedOptions?: any[];
}

export interface DropdownItemFilterInterface {
  /**
   * preCondition output determinates to execute condition or not.
   *
   * For example: You don't want to filter items if all items has the date field null.
   * You can check this on filter condition, but it will be expensive and unnecessary.
   * This preCondition function solves that.
   *
   * @param subject Usually the main array or main object being processed in the component.
   *
   */
  preCondition?: (subject: any) => boolean;

  /**
   * Condition to be used in Array.prototype.filter()
   *
   * @param element The current element being processed in the array.
   *
   * @param index The index of the current element being processed in the array.
   *
   * @param array The array filter was called upon.
   *
   * */
  condition: (element: unknown, index?: number, array?: unknown[]) => boolean;
}

export interface SearchBoxSettingsInterface extends CommonSettingsInterface {
  maxLength?: number;
  placeHolder?: string;
  cls?: string;
}

export interface ButtonGroupSettingsInterface extends CommonSettingsInterface {
  buttons: ScwMatButtonGroupButtons[];
  value?: any;
}

export interface CreatedChangedCRUDInterface {
  createdBy: number;
  createdAt: Date;
  changedBy: number;
  changedAt: Date;
}

export interface UsersCRUDInterface {
  id?: number;
  userName: string;
  fullName: string;
  email: string;
  password: string;
  currentLine: string;
  currentSite: string;
  checkInCardId: string;
  checkInsPin: string;
  details: string;
  multipleCheckIns: number;
  phone: string;
  defaultSite: number;
  levelId: number;
  isActive: number;
  isAutoCheckOutActive: number;
  userLanguage: string;
  timeZone: string;
  locale: string;
  dateFormat: string;
  dateTimeFormat: string;
  contactIt: boolean;
  contactSupervisor: boolean;
  contactMaintenance: boolean;
  contactQuality: boolean;
  contactScw: boolean;
  contactWarehouse: boolean;
  contactType: string;
  hasBroaderSiteLineAuth?: boolean;
}

export interface DeviceTypeCRUDInterface {
  id?: number;
  name: string;
  status: number;
}
export interface ClientCRUDInterface {
  id?: string;
  name: string;
  status: number;
}

export interface UserDropdownItemInterface extends UsersCRUDInterface {
  name: string;
}

export interface ObservationCRUDInterface {
  name: string;
  id: number;
}

export interface ServiceResponseInterface<T> {
  data: T[];
  success: boolean;
}

export class FilterClass implements FilterClassInterface {
  public objectNameProp: string = 'name';
  public subscribe: Subscription = null;
  public data: FilterDataObjectTypes[];
  public dataSubject: Subject<any> = new Subject<object>();
  public apiServer = ApiServer.NestJS;

  public setApiServer(server: ApiServer) {
    this.apiServer = server;
  }

  public getDataObservable(): Observable<FilterDataObjectTypes[]> {
    return this.dataSubject.asObservable();
  }

  public getObjectNameProp(): string {
    return this.objectNameProp;
  }

  public getDataWithInitialData(initialData: FilterDataObjectTypes[]): FilterDataObjectTypes[] {
    return this.data;
  }

  init(): void {}

  getInitialData(): FilterDataObjectTypes[] {
    return undefined;
  }

  getFilterData(options: HttpOptionInterface): void {
    console.warn('Your getFilterData method not found in your filter class.');
  }

  destroy(): void {}
}

export type FilterConditionTypes =
  | '$eq'
  | '$ne'
  | '$gt'
  | '$lt'
  | '$gte'
  | '$lte'
  | '$starts'
  | '$ends'
  | '$cont'
  | '$excl'
  | '$in'
  | '$notin'
  | '$isnull'
  | '$notnull'
  | '$between'
  | '$eqL'
  | '$neL'
  | '$startsL'
  | '$endsL'
  | '$contL'
  | '$exclL'
  | '$inL'
  | '$notinL';

export interface ISourceObject {
  sourceObjectId: number;
  destinationTypeId: number;
  destinationObjectId: number;
}

export interface IUserObject extends ISourceObject {
  username: string;
}
export interface IEquipmentObject extends ISourceObject {
  equipment: string;
}
